import React from 'react';
import { Autocomplete, CircularProgress, TextField, debounce } from '@mui/material';
import { getCompaniesApiMethod } from 'lib/api';

const autocompleteService = { current: null };

interface Company {
  name: string;
}

type Props = {
  selectedCompany?: string;
  namesToExclude?: string;
  onSelect: any;
};

const ADD_NEW_PREFIX = 'Add new company: ';

export default function CompaniesAutoComplete({
  selectedCompany,
  namesToExclude,
  onSelect,
}: Props) {
  const [loading, setLoading] = React.useState(false);
  const [value, setValue] = React.useState<Company | undefined>(selectedCompany);
  const [inputValue, setInputValue] = React.useState('');
  const [options, setOptions] = React.useState<readonly Company[]>([]);

  const fetch = React.useMemo(
    () =>
      debounce((request: { input: string }, callback: (results?: readonly Company[]) => void) => {
        setLoading(true);

        (autocompleteService.current as any)
          .getCompaniesApiMethod(
            {
              page: null,
              search: request.input,
              names_exclude: namesToExclude,
            },
            {},
          )
          .then((data) => {
            setLoading(false);
            callback(data.companies);
          });
      }, 400),
    [],
  );

  const setOptionsFromFetch = (results) => {
    let newOptions: readonly Company[] = [];

    if (value) {
      newOptions = [value];
    }

    if (results) {
      newOptions = [...newOptions, ...results];
    }
    setOptions(newOptions);
  };

  React.useEffect(() => {
    let active = true;

    if (!autocompleteService.current) {
      autocompleteService.current = {
        getCompaniesApiMethod,
      };
    }

    if (inputValue === '') {
      setOptions(value ? [value] : []);
      return undefined;
    }

    fetch({ input: inputValue }, (results?: readonly Company[]) => {
      if (active) {
        setOptionsFromFetch(results);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetch]);

  return (
    <Autocomplete
      isOptionEqualToValue={(option, value) => option.name === value.name}
      getOptionLabel={(option) => option.name}
      options={options}
      autoComplete
      includeInputInList
      filterSelectedOptions
      value={value}
      noOptionsText="Type a company name ..."
      onChange={(_event, newValue: Company | null) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);

        if (newValue) {
          onSelect({
            ...newValue,
            name: newValue.name.replace(ADD_NEW_PREFIX, ''),
          });
        } else {
          onSelect(null);
        }
      }}
      onInputChange={(_event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          id="company-auto-complete"
          {...params}
          label="Select company*"
          name={Math.random().toString()}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
            autoComplete: 'new-company-auto-complete',
          }}
        />
      )}
    />
  );
}
